import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { alignItems, display, height, maxWidth, minWidth } from 'styled-system';
import type { AlignItemsProps, DisplayProps, MaxWidthProps, MinWidthProps } from 'styled-system';

import Carousel from 'common/components/Carousel';
import ShowAllLink from 'common/components/Carousel/controls/ShowAllLink';
import type { DyTrackingGA4Props } from 'common/tracking/getDyTrackingGA4Params';
import type { SeeAllLinkProp, VisualNavigationItemData } from 'common/types/visual-navigation';
import { useCarouselConfig } from './hooks/carousel-config';
import { useTrackingEvents, useViewTrackingEvent } from './hooks/tracking';
import { VisualNavigationItem } from './VisualNavigationItem';

const SLIDE_WIDTH = [132, 200, 228, 248];

interface VisualNavigationProps extends SeeAllLinkProp {
  items: VisualNavigationItemData[];
  withBackground?: boolean;
  forceVisibleSlidesCount?: number;
  title?: string;
  tracking?: DyTrackingGA4Props;
  imageRatio?: number;
  subtitle?: string;
}

const ContainerRef = styled.div<any>`
  ${height};
`;

const SlideContainer = styled.section<
  MinWidthProps & MaxWidthProps & DisplayProps & AlignItemsProps
>`
  ${minWidth};
  ${maxWidth};
  ${display};
  ${alignItems};
`;

export const VisualNavigation = ({
  items,
  tracking,
  withBackground,
  imageRatio,
  seeAllLink,
  title,
  subtitle,
}: VisualNavigationProps) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const { slidesToShow } = useCarouselConfig(withBackground);

  const { ref } = useViewTrackingEvent({
    title,
    items,
    activeSlide,
    slidesToShow,
    tracking,
  });

  const { onSeeAllLinkClick } = useTrackingEvents({
    title,
    linkText: seeAllLink?.label,
    linkUrl: seeAllLink?.href,
    tracking,
  });

  const handleAfterSlide = useCallback(
    (currentSlide: number) => {
      setActiveSlide(currentSlide);
    },
    [activeSlide]
  );

  return (
    <>
      <ContainerRef ref={ref} height="1px" />
      <Carousel afterSlide={handleAfterSlide} title={title} subtitle={subtitle} withTopControls>
        {items.map((itemProps, index) => (
          <SlideContainer key={index} minWidth={SLIDE_WIDTH} maxWidth={SLIDE_WIDTH}>
            <VisualNavigationItem
              {...itemProps}
              key={`slide-${index}`}
              index={index}
              itemsCount={items.length}
              withDataPrefetch={index - activeSlide <= slidesToShow}
              withBackground={withBackground}
              imageRatio={imageRatio}
              {...(title ? { title } : {})}
              {...(tracking ? { tracking } : {})}
            />
          </SlideContainer>
        ))}
        {seeAllLink?.href && seeAllLink?.label && (
          <SlideContainer
            key={items.length}
            minWidth="fit-content"
            display="flex"
            alignItems="center"
          >
            <ShowAllLink
              data-testid="see-all-link"
              onClick={onSeeAllLinkClick}
              text={seeAllLink?.label}
              url={seeAllLink?.href}
            />
          </SlideContainer>
        )}
      </Carousel>
    </>
  );
};
